import {
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  addToast,
  Modal,
  DateRangePicker,
  Skeleton,
} from "@heroui/react";
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import { i18n } from "@lingui/core";
import { api } from "@/lib/api-client";
import { getApiErrorMessage } from "@/utils/api-error";
import { LimeInput } from "@/Components/NextBase/LimeInput";
import { fromDate, CalendarDate } from "@internationalized/date";
import { useEffect, useRef, useState } from "react";
import { useForm } from "@mantine/form";
import dayjs from "dayjs";
import {
  CommitmentWarningDialog,
  isUnderCommitment,
} from "@/Components/CommitmentWarningDialog";
import { commitmentTermShiftLabel } from "@/pages/Dashboard/Subscriptions/commitmentPlanTerm";
import {
  getValidFromLabel,
  getValidToLabel,
  type ValidityRange,
} from "./subscriptionValidityLabels";

type ExtensionFormValues = {
  dateRange: ValidityRange;
  notes: string;
};

interface CustomerSubscriptionExtensionModalProps {
  isOpen: boolean;
  onClose: () => void;
  customerSubscriptionId: number;
}

const toCalendarDate = (zoned: ReturnType<typeof fromDate>): CalendarDate =>
  new CalendarDate(zoned.year, zoned.month, zoned.day);

export const CustomerSubscriptionExtensionModal = ({
  isOpen,
  onClose,
  customerSubscriptionId,
}: CustomerSubscriptionExtensionModalProps) => {
  const wasOpenRef = useRef(isOpen);
  const hydratedRangeKeyRef = useRef<string | null>(null);
  const { locale } = i18n;
  const { mutateAsync: updateSubscription, isPending: isUpdatingSubscription } =
    api.customer.useUpdateCustomerSubscription();

  // Holds the values the warning was raised for, so confirming saves that change even if
  // the form has moved on underneath it.
  const [pendingConfirmation, setPendingConfirmation] =
    useState<ExtensionFormValues | null>(null);

  const { data: customerSubscriptionResponse } =
    api.customer.useGetCustomerSubscription({ customerSubscriptionId });

  const validFromDate = customerSubscriptionResponse?.customerSubscription
    .validFrom
    ? new Date(customerSubscriptionResponse.customerSubscription.validFrom)
    : new Date();
  const validToDate = customerSubscriptionResponse?.customerSubscription.validTo
    ? new Date(customerSubscriptionResponse.customerSubscription.validTo)
    : new Date();

  const currentValidFromCalendarDate = toCalendarDate(
    fromDate(validFromDate, "UTC"),
  );
  const currentValidToCalendarDate = toCalendarDate(
    fromDate(validToDate, "UTC"),
  );

  const form = useForm<ExtensionFormValues>({
    initialValues: {
      dateRange: {
        start: currentValidFromCalendarDate,
        end: currentValidToCalendarDate,
      },
      notes: "",
    },
    validate: {
      dateRange: (value) => {
        if (!value) {
          return t`Izberite obdobje veljavnosti.`;
        }

        const validTo = dayjs(value.end.toDate("UTC")).endOf("day");

        if (validTo.isBefore(dayjs().startOf("day"))) {
          return t`Končni datum veljavnosti ne sme biti v preteklosti.`;
        }

        return null;
      },
    },
  });

  const validFromLabel = getValidFromLabel(form.values.dateRange, locale);
  const validToLabel = getValidToLabel(form.values.dateRange, locale);

  useEffect(() => {
    if (!isOpen) {
      wasOpenRef.current = false;
      hydratedRangeKeyRef.current = null;
      return;
    }

    if (!customerSubscriptionResponse) return;
    const rangeKey = `${customerSubscriptionResponse.customerSubscription.validFrom}:${customerSubscriptionResponse.customerSubscription.validTo}`;
    const shouldHydrate =
      !wasOpenRef.current || hydratedRangeKeyRef.current !== rangeKey;

    if (!shouldHydrate) {
      return;
    }

    form.setFieldValue("dateRange", {
      start: currentValidFromCalendarDate,
      end: currentValidToCalendarDate,
    });
    hydratedRangeKeyRef.current = rangeKey;
    wasOpenRef.current = true;
  }, [
    currentValidFromCalendarDate,
    currentValidToCalendarDate,
    customerSubscriptionResponse,
    isOpen,
    form,
  ]);

  const underCommitment = isUnderCommitment(
    customerSubscriptionResponse?.customerSubscription.commitmentLock,
  );

  const submitValidity = async (values: ExtensionFormValues) => {
    if (!values.dateRange) return;

    try {
      await updateSubscription({
        customerSubscriptionId,
        body: {
          validFrom: values.dateRange.start.toString(),
          validTo: values.dateRange.end.toString(),
          notes: values.notes || t`Spremenjena veljavnost paketa`,
        },
      });

      addToast({
        title: t`Veljavnost paketa spremenjena`,
        description: t`Veljavnost paketa je bila uspešno spremenjena.`,
        color: "success",
      });

      handleClose();
    } catch (error) {
      addToast({
        title: t`Napaka`,
        description:
          getApiErrorMessage(error) ??
          t`Prišlo je do napake. Poskusite ponovno.`,
        color: "danger",
      });
    } finally {
      setPendingConfirmation(null);
    }
  };

  const handleSubmit = (values: ExtensionFormValues) => {
    if (underCommitment) {
      setPendingConfirmation(values);
      return;
    }
    void submitValidity(values);
  };

  const handleClose = () => {
    form.reset();
    setPendingConfirmation(null);
    onClose();
  };

  const confirmationRange = pendingConfirmation?.dateRange ?? null;

  const { data: validityPreview, isPending: isValidityPreviewPending } =
    api.customer.useGetCustomerSubscriptionValidityPreview({
      customerSubscriptionId,
      validFrom: confirmationRange?.start.toString() ?? "",
      validTo: confirmationRange?.end.toString() ?? "",
      enabled: confirmationRange != null,
    });

  const termShiftConsequence = (() => {
    if (confirmationRange == null) {
      return null;
    }
    if (isValidityPreviewPending) {
      return (
        <Skeleton
          data-identifier="commitment-term-shift-loading"
          className="h-4 w-3/4 rounded-md"
        />
      );
    }

    const label =
      validityPreview?.termEnds
        .map(commitmentTermShiftLabel)
        .find((shift) => shift != null) ?? null;

    return label == null ? null : (
      <p
        data-identifier="commitment-term-shift"
        className="text-warning text-sm"
      >
        {label}
      </p>
    );
  })();

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        isDismissable={!isUpdatingSubscription}
        isKeyboardDismissDisabled={isUpdatingSubscription}
        hideCloseButton={isUpdatingSubscription}
        data-identifier="extension-modal"
      >
        <ModalContent>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              e.stopPropagation();

              form.validate();
              if (form.isValid()) {
                handleSubmit(form.getValues());
              }
            }}
          >
            <ModalHeader>
              <div className="flex flex-col justify-between">
                <Trans>Sprememba veljavnosti paketa</Trans>
                <p className="text-xs font-medium">
                  {customerSubscriptionResponse?.customerSubscription.variation
                    ?.name
                    ? customerSubscriptionResponse.customerSubscription
                        .variation.name
                    : ""}
                </p>
              </div>
            </ModalHeader>
            <ModalBody>
              <div className="flex flex-col gap-4">
                <DateRangePicker
                  data-identifier="extension-date-range"
                  label={t`Izberi novo obdobje veljavnosti`}
                  variant="bordered"
                  labelPlacement="outside"
                  errorMessage={
                    typeof form.errors.dateRange === "string"
                      ? form.errors.dateRange
                      : undefined
                  }
                  isInvalid={!!form.errors.dateRange}
                  description={
                    validFromLabel && validToLabel ? (
                      <Trans>
                        Paket bo veljaven od {validFromLabel} do {validToLabel}
                      </Trans>
                    ) : undefined
                  }
                  {...form.getInputProps("dateRange")}
                />
                <LimeInput
                  label={t`Opombe`}
                  placeholder={t`Dodajte opombo o spremembi veljavnosti...`}
                  variant="bordered"
                  labelPlacement="outside"
                  {...form.getInputProps("notes")}
                />
              </div>
            </ModalBody>
            <ModalFooter>
              <Button
                variant="light"
                onPress={handleClose}
                isDisabled={isUpdatingSubscription}
              >
                <Trans>Prekliči</Trans>
              </Button>
              <Button
                data-identifier="extension-submit-btn"
                color="primary"
                variant="solid"
                type="submit"
                isLoading={isUpdatingSubscription}
              >
                <Trans>Potrdi spremembe</Trans>
              </Button>
            </ModalFooter>
          </form>
        </ModalContent>
      </Modal>
      <CommitmentWarningDialog
        isOpen={pendingConfirmation != null}
        isSaving={isUpdatingSubscription}
        consequence={termShiftConsequence}
        onDismiss={() => setPendingConfirmation(null)}
        onConfirm={() => {
          if (pendingConfirmation) {
            void submitValidity(pendingConfirmation);
          }
        }}
      />
    </>
  );
};
